/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { IconExclamationCircle } from "@tabler/icons-react";
import { useForm } from "react-hook-form";
import { use3PLListData } from "../../apis/3plApis";
import SelectInput from "../../components/Select";
import { useWarehouseListData } from "../../apis/warehouseApi";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import RoleToggleTabs from "../../components/RoleToggleTabs";
import BulkStatusRevertModal from "./_components/BulkStatusRevertModal";
import FmBulkUploadModals from "./_components/FmBulkUploadModals";
import BulkResponseModal from "../../components/BulkResponseModal";
import { BULK_COLUMNS } from "../../constants/bulkUploadColumns";
import { VALIDATION_MAPPING } from "../../constants/validationMapping";

const roles = ["Forward", "FM Bulk Orders", "Bulk Status Revert"];

const fmOrderColumns = BULK_COLUMNS.FM_ORDERS;
const fmBulkResponseConfig = {
  failKey: "errors" as const,
  failColumns: [
    { key: "reference_no", header: "Reference No" },
    { key: "receiver_mobile", header: "Receiver Phone" },
    { key: "reason", header: "Reason" },
  ],
  allColumns: [...fmOrderColumns, { key: "reason", header: "Reason" }],
  sheetName: "Failed FM Orders",
  downloadFileName: "failed-fm-orders",
  validationMessages: fmOrderColumns.map(
    (column) => VALIDATION_MAPPING[column.key] ?? column.key,
  ),
};

const Forward: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isWebhookLoading, setIsWebhookLoading] = useState(false);
  const activeRole = searchParams.get("tab") || "Forward";
  const [rowCount, setRowCount] = useState(0);
  const [showFmBulkModal, setShowFmBulkModal] = useState(false);
  const [fmBulkResponse, setFmBulkResponse] = useState<any>(null);
  const [openFmResponse, setOpenFmResponse] = useState<boolean | string>(false);
  const { data: shippingPartners } = use3PLListData();
  const {
    data: warehouses,
    isPending: listPending,
    refetch: warehouseRefetch,
  } = useWarehouseListData();
  const profile = useSelector((state: any) => state.auth.profile);

  const handleRoleChange = (role: string) => {
    setSearchParams({ tab: role });
  };

  // ✅ react-hook-form setup
  const {
    register,
    handleSubmit,
    watch,
    clearErrors,
    setValue,
    reset,
    formState: { errors },
  } = useForm();

  // Watch AWB textarea value
  const awbValue = watch("awbs", "");

  // ✅ Auto update row count when AWBs change
  useEffect(() => {
    if (!awbValue) {
      setRowCount(0);
      return;
    }
    const list = awbValue
      .split(/\s+/)
      .map((awb: string) => awb.trim())
      .filter((awb: string) => awb.length > 0);

    const uniqueList = Array.from(new Set(list));
    setRowCount(uniqueList.length);
  }, [awbValue]);

  const onSubmit = async (data: any) => {
    // Clean AWBs before submit
    const awbList = data.awbs
      ?.split(/\s+/)
      .map((awb: string) => awb.trim())
      .filter((awb: string) => awb.length > 0);

    const uniqueAwbs = Array.from(new Set(awbList));

    const payload = { ...data, awbs: uniqueAwbs, clientId: profile?.id };
    const shippingPartner = shippingPartners?.find(
      (shippingPartner: any) => shippingPartner?.id === data["3plCompany"],
    );

    if (shippingPartner?.webhookUrl) {
      // Define the payload to be sent
      // const payload = {
      //   event_type: 'order.forwarded',
      //   clientId: profile?.id,
      //   payload: { orderId: order?.id }
      // };
      setIsWebhookLoading(true);

      try {
        // Make sure the webhook URL exists
        if (!shippingPartner?.webhookUrl) {
          setIsWebhookLoading(false);
          console.error("Webhook URL is missing.");
          return;
        }

        const response = await fetch(
          `${shippingPartner.webhookUrl}/bulk-forward`,
          {
            method: "POST", // ⭐ Specify the method as POST
            headers: {
              "Content-Type": "application/json", // ⭐ Tell the server the body is JSON
            },
            credentials: "include",
            body: JSON.stringify(payload), // ⭐ Convert the data object to a JSON string
          },
        );

        // Handle the response
        if (response.ok) {
          // The webhook was successfully delivered and accepted
          const responseData = await response.json();
          reset();
          toast.success("Order Forwarded successfully");
        } else {
          // The server responded with an error status (4xx or 5xx)
          const errorData = await response.json();
          console.error(
            "❌ Failed to send webhook:",
            response.status,
            errorData,
          );
        }
      } catch (error) {
        // Handle network errors or other exceptions
        console.error("❌ Network or fetch error:", error);
      } finally {
        // Always set loading to false, regardless of outcome
        setIsWebhookLoading(false);
      }
    }
  };

  return (
    <div className="p-4">
      <div className="flex justify-between items-center">
        {/* <div className="flex items-center gap-2">
          <button
            onClick={() => navigate(-1)}
            className="p-1.5 border border-[#D2D2D2] rounded-full hover:bg-[#F7F6F2] transition"
          >
            <IconChevronLeft strokeWidth={1.5} size={22} />
          </button>
          <h1 className="text-xl text-[#212121] tracking-[-0.5px] font-medium">
            Forward
          </h1>
        </div> */}

        <RoleToggleTabs
          options={roles}
          active={activeRole}
          onChange={handleRoleChange}
        />
      </div>

      {activeRole === "Forward" && (
        <div>
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="bg-[#F7F6F2] rounded-r-[30px]  rounded-bl-[30px] p-6 space-y-6"
          >
            {/* Info box */}
            <div className="bg-[#FBD037]/20 border border-[#FBD037] p-4 rounded-xl flex items-start gap-2">
              <IconExclamationCircle />
              <div className="space-y-1">
                <p className="text-[#212121] text-sm">
                  1. Duplicate Orders are automatically removed.
                </p>
                <p className="text-[#212121] text-sm">
                  2. You can forward up to 20 shipments at once!
                </p>
              </div>
            </div>

            {/* Paste AWBs */}
            <div className="bg-[#FCFDF8] border border-[#D2D2D2] rounded-xl p-4 h-48">
              <textarea
                {...register("awbs")}
                placeholder="Paste AWB numbers here (each AWB must be on a new line or separated by space)."
                className="w-full h-full bg-transparent outline-none resize-none text-sm text-[#212121] placeholder:text-gray-400"
              />
            </div>

            {/* Form inputs */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6 items-end">
              <SelectInput
                name="3plCompany"
                label="Select 3PL Company"
                placeholder="Select"
                selectOptions={
                  shippingPartners?.map((company: any) => ({
                    id: company?.id,
                    value: company?.id,
                    text: company?.company_name,
                  })) || []
                }
                register={register}
                clearErrors={clearErrors}
                validation={{ required: "3PL Company is required" }}
                error={errors["3plCompany"]}
                disabled={!shippingPartners || shippingPartners?.length === 0}
                value={watch("3plCompany")}
                setValue={(val: string) => setValue("3plCompany", val)}
              />

              <SelectInput
                name="warehouse"
                label="Select Warehouse"
                placeholder="Select"
                selectOptions={
                  warehouses?.warehouses?.map((warehouse: any) => ({
                    id: warehouse?.id,
                    value: warehouse?.id,
                    text: warehouse?.name,
                  })) || []
                }
                register={register}
                clearErrors={clearErrors}
                validation={{ required: "Warehouse is required" }}
                error={errors.warehouse}
                disabled={!warehouses || warehouses?.warehouses?.length === 0}
                value={watch("warehouse")}
                setValue={(val: string) => setValue("warehouse", val)}
              />

              {/* Actions */}
              <div className="flex items-center gap-3 col-span-2 md:col-span-1 md:justify-end">
                <span className="text-sm font-medium text-[#212121]">
                  Total AWBs Added: {rowCount}
                </span>
                <button
                  type="submit"
                  disabled={isWebhookLoading}
                  className="bg-[#FBD037]  text-black text-sm font-medium px-6 py-2 rounded-full shadow-sm hover:bg-[#d1a306] transition"
                >
                  Submit
                </button>
              </div>
            </div>
          </form>
        </div>
      )}
      {activeRole === "Forward Reverse" && (
        <div>
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="bg-[#F7F6F2] rounded-r-[30px]  rounded-bl-[30px] p-6 space-y-6"
          >
            {/* Info box */}
            <div className="bg-[#FBD037]/20 border border-[#FBD037] p-4 rounded-xl flex items-start gap-2">
              <IconExclamationCircle />
              <div className="space-y-1">
                <p className="text-[#212121] text-sm">
                  1. Duplicate Orders are automatically removed.
                </p>
                <p className="text-[#212121] text-sm">
                  2. You can forward up to 20 shipments at once!
                </p>
              </div>
            </div>

            {/* Paste AWBs */}
            <div className="bg-[#FCFDF8] border border-[#D2D2D2] rounded-xl p-4 h-48">
              <textarea
                {...register("awbs")}
                placeholder="Paste AWB numbers here (each AWB must be on a new line or separated by space)."
                className="w-full h-full bg-transparent outline-none resize-none text-sm text-[#212121] placeholder:text-gray-400"
              />
            </div>

            {/* Form inputs */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6 items-end">
              <SelectInput
                name="3plCompany"
                label="Select 3PL Company"
                placeholder="Select"
                selectOptions={
                  shippingPartners?.map((company: any) => ({
                    id: company?.id,
                    value: company?.id,
                    text: company?.company_name,
                  })) || []
                }
                register={register}
                clearErrors={clearErrors}
                validation={{ required: "3PL Company is required" }}
                error={errors["3plCompany"]}
                disabled={!shippingPartners || shippingPartners?.length === 0}
                value={watch("3plCompany")}
                setValue={(val: string) => setValue("3plCompany", val)}
              />

              <SelectInput
                name="warehouse"
                label="Select Warehouse"
                placeholder="Select"
                selectOptions={
                  warehouses?.warehouses?.map((warehouse: any) => ({
                    id: warehouse?.id,
                    value: warehouse?.id,
                    text: warehouse?.name,
                  })) || []
                }
                register={register}
                clearErrors={clearErrors}
                validation={{ required: "Warehouse is required" }}
                error={errors.warehouse}
                disabled={!warehouses || warehouses?.warehouses?.length === 0}
                value={watch("warehouse")}
                setValue={(val: string) => setValue("warehouse", val)}
              />

              {/* Actions */}
              <div className="flex items-center gap-3 col-span-2 md:col-span-1 md:justify-end">
                <span className="text-sm font-medium text-[#212121]">
                  Total AWBs Added: {rowCount}
                </span>
                <button
                  type="submit"
                  disabled={isWebhookLoading}
                  className="bg-[#FBD037]  text-black text-sm font-medium px-6 py-2 rounded-full shadow-sm hover:bg-[#d1a306] transition"
                >
                  Submit
                </button>
              </div>
            </div>
          </form>
        </div>
      )}
      {activeRole === "Bulk Status Revert" && (
        <div>
          <BulkStatusRevertModal />
        </div>
      )}
      {activeRole === "FM Bulk Orders" && (
        <div>
          {showFmBulkModal && (
            <FmBulkUploadModals
              onClose={() => setShowFmBulkModal(false)}
              onResponseOpene={() => setOpenFmResponse(true)}
              setOrderBulkResponse={setFmBulkResponse}
            />
          )}
          {openFmResponse && fmBulkResponse && (
            <BulkResponseModal
              data={fmBulkResponse}
              config={fmBulkResponseConfig}
              onClose={() => setOpenFmResponse(false)}
            />
          )}
          <div className="bg-[#F7F6F2] rounded-r-[30px] rounded-bl-[30px] p-6">
            <button
              onClick={() => setShowFmBulkModal(true)}
              className="bg-[#FBD037] text-black text-sm font-medium px-6 py-2 rounded-full shadow-sm hover:bg-[#d1a306] transition"
            >
              Upload FM Orders
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Forward;
